import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {
  isSettlementPaymentMethodArchived,
  readSettlementAccountField,
} from '@/types';
import * as formatDate from '@/utils/date';

import SettlementAccountArchiveButton from './SettlementAccountArchiveButton';
import SettlementAccountUpdateButton from './SettlementAccountUpdateButton';
import {
  settlementChannelFields,
  settlementChannelLabel,
} from './settlementChannelForms';

type SettlementAccountSummaryProps = {
  item: SettlementPaymentMethod;
  /** Called after an edit or an archive so the list refreshes in place. */
  onSaved?: () => void | Promise<void>;
};

const Row = ({label, value}: {label: string; value: string}) => (
  <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
    <p className="text-slate-500 text-sm dark:text-slate-100">{label}</p>
    <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
      {value}
    </p>
  </div>
);

/**
 * The expanded settlement account row. Fields come from the channel registry
 * rather than a fixed list, so a mobile money account shows its own fields and
 * a new channel needs no change here.
 */
const SettlementAccountSummary = ({
  item,
  onSaved,
}: SettlementAccountSummaryProps) => {
  const isArchived = isSettlementPaymentMethodArchived(item);

  return (
    <div
      className="whitespace-normal break-words bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
      data-cy="merchant-settlement-accounts-summary"
    >
      <div className="w-full">
        <Row label="Channel" value={settlementChannelLabel(item.channel)} />
        <Row label="Currency" value={String(item.currencyCode)} />

        {settlementChannelFields(item.channel).map((field) => (
          <Row
            key={field.name}
            label={field.label}
            value={readSettlementAccountField(item.info, field.name)}
          />
        ))}

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Status</p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-settlement-accounts-summary-status"
          >
            {isArchived ? (
              <span className="text-red-500">Archived</span>
            ) : (
              <span className="text-green-500">Active</span>
            )}
          </p>
        </div>

        <Row
          label="Last updated"
          value={formatDate.DDMonYYYY(String(item.updatedAt))}
        />
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.SettlementPaymentMethodManage}
          >
            <SettlementAccountUpdateButton
              initialData={item}
              onSuccess={onSaved}
            />
            <SettlementAccountArchiveButton item={item} onSuccess={onSaved} />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default SettlementAccountSummary;
